import React, { lazy, Suspense, useEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { safe_get } from "../../../report-visuals/report-utils";
import { Disclosure } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { classNames, useStore } from "../../../helpers";
import { QuestionAndAnswerResponseView } from "../../../features/survey-management/question-and-answer-view";
import { useTranslation } from "react-i18next";
import { LoadingState } from "../../../components";
import { Button } from "@mui/material";
import { PlayCircleOutline } from "@mui/icons-material";
import _ from "lodash";

const AudioPlayer = lazy(() =>
  import("../../../features/survey-management/audio-player")
);

interface DocumentDetailViewProps {
  documentId: string | null;
  documentDetails: any;
  loading: boolean;
}

export const DocumentDetailView: React.FC<DocumentDetailViewProps> = observer(
  ({ documentId, documentDetails, loading }) => {
    const { surveyResponseStore } = useStore();
    const { t } = useTranslation("survey_management");
    const [showAudio, setShowAudio] = useState(false);
    const audioPlayer = useRef(null);

    useEffect(() => {
      setShowAudio(false);
    }, [documentId]);

    if (!documentId || !documentDetails) {
      return (
        <div className="bg-white rounded-2xl shadow-lg p-12 text-center border border-gray-100">
          <div className="text-5xl mb-4">📄</div>
          <p className="text-gray-600 text-lg">Select a document to view details</p>
        </div>
      );
    }

    if (loading) {
      return (
        <div className="bg-white rounded-2xl shadow-lg p-12 border border-gray-100">
          <LoadingState />
        </div>
      );
    }

    const { sr } = documentDetails;
    if (!sr) {
      return (
        <div className="bg-white rounded-2xl shadow-lg p-12 text-center border border-gray-100">
          <div className="text-5xl mb-4">⚠️</div>
          <p className="text-gray-600 text-lg">No details available for this document</p>
        </div>
      );
    }

    const { response, meta, voterref } = sr;
    const responses = response?.["response"] || [];
    const { surveyref, nav } = meta || {};
    const { questions } = surveyref || {};

    const onQuestionClick = (time: number, question: any) => {
      if (audioPlayer.current != null) {
        (audioPlayer.current as any).seek(time);
      }
      surveyResponseStore.set_active_question(question);
    };

    const renderQuestionResponses = () => {
      if (!questions || !responses || questions.length !== responses.length) {
        return null;
      }

      const elements = [];
      for (let ind = 0; ind < questions.length; ind++) {
        const q = questions[ind];
        const r = responses[ind];

        const matches = _.filter(nav, {
          to: safe_get(nav[ind + 1], "to", "0"),
        });
        if (matches.length == 0) continue;
        const match = matches[0];
        let offset = match["ts"] - nav[0]["ts"];
        offset /= 1000;

        elements.push(
          <QuestionAndAnswerResponseView
            key={ind}
            qno={ind}
            question={q}
            seek={offset}
            response={r}
            onClickHandler={onQuestionClick}
          />
        );
      }
      return <div className="flex flex-col">{elements}</div>;
    };

    const question_and_answer_accordion = () => {
      return (
        <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl mt-4">
          <div className="mx-auto p-4">
            <dl className="divide-y divide-gray-200">
              <Disclosure as="div">
                {({ open }) => (
                  <>
                    <dt className="text-lg">
                      <Disclosure.Button className="text-left w-full flex justify-between items-center p-4 hover:bg-indigo-50/50 rounded-lg transition-colors duration-200">
                        <div className="flex items-center gap-3">
                          <div className="h-1 w-6 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full"></div>
                          <span className="block text-base font-bold text-indigo-700 uppercase tracking-wide">
                            {t("question_answers")}
                          </span>
                        </div>
                        <ChevronDownIcon
                          className={classNames(
                            open ? "-rotate-180" : "rotate-0",
                            "h-6 w-6 text-indigo-600 transform transition-transform duration-300"
                          )}
                          aria-hidden="true"
                        />
                      </Disclosure.Button>
                    </dt>
                    <Disclosure.Panel as="dd" className="mt-2 px-4 pb-4">
                      <div className="bg-white rounded-lg p-4 border border-gray-200">
                        {renderQuestionResponses()}
                      </div>
                    </Disclosure.Panel>
                  </>
                )}
              </Disclosure>
            </dl>
          </div>
        </div>
      );
    };

    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 h-full flex flex-col">
        <div className="flex items-center gap-2 mb-5">
          <div className="h-1 w-1.5 bg-gradient-to-b from-indigo-500 to-purple-600 rounded-full"></div>
          <label className="block text-base font-semibold text-gray-800">
            {t("survey_details")}
          </label>
        </div>

        {!showAudio && (
          <div className="flex justify-start my-4">
            <Button
              variant="contained"
              color="primary"
              startIcon={<PlayCircleOutline />}
              onClick={() => setShowAudio(true)}
              sx={{
                borderRadius: "12px",
                padding: "12px 24px",
                fontWeight: "bold",
                textTransform: "none",
                fontSize: "16px",
                background: "linear-gradient(90deg, #4F46E5 0%, #7C3AED 100%)",
                boxShadow: "0px 4px 16px rgba(79, 70, 229, 0.3)",
                transition: "all 0.3s ease",
                "&:hover": {
                  background: "linear-gradient(90deg, #4338CA 0%, #6D28D9 100%)",
                  boxShadow: "0px 6px 20px rgba(79, 70, 229, 0.4)",
                  transform: "translateY(-2px)",
                },
              }}
            >
              See Audio Response
            </Button>
          </div>
        )}

        <Suspense fallback={<LoadingState />}>
          {showAudio && (
            <>
              {documentDetails["s3uri"] ? (
                <AudioPlayer
                  data={documentDetails}
                  ref={audioPlayer}
                />
              ) : (
                <div className="p-4 text-gray-600 italic text-center">
                  {t("no_audio_available")}
                </div>
              )}
            </>
          )}

          {sr ? (
            <>
              {question_and_answer_accordion()}
            </>
          ) : null}
        </Suspense>
      </div>
    );
  }
);
